import CSS from "csstype";
import React, { useEffect, useRef, useState } from "react";
import { IGetStateCard } from "~src/user/apigen/getState";

export type BombAnimationEvent = {
  id: string;
  cards: IGetStateCard[];
};

type BombAnimationProps = {
  event: BombAnimationEvent | null;
  onComplete: () => void;
};

const dragonDurationMs = 2600;
const dragonWidth = 520;
const dragonHeight = 112;
const dragonGifSource = "/flamebreathing.gif";

let dragonGifBlob: Blob | null = null;
let dragonGifPromise: Promise<Blob> | null = null;

export const BombAnimation: React.FC<BombAnimationProps> = ({ event, onComplete }) => {
  const activeURLRef = useRef<string | null>(null);
  const [gifSource, setGifSource] = useState<string | null>(null);

  useEffect(() => {
    void loadDragonGifBlob();

    return () => {
      revokeObjectURL(activeURLRef.current);
      activeURLRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (event === null) {
      return;
    }

    let active = true;
    let completionTimeout = 0;

    revokeObjectURL(activeURLRef.current);
    activeURLRef.current = null;
    setGifSource(null);

    void loadDragonGifBlob()
      .then(blob => {
        if (!active) {
          return;
        }

        const objectURL = window.URL.createObjectURL(blob);
        activeURLRef.current = objectURL;
        setGifSource(objectURL);
      })
      .catch(() => {
        completionTimeout = window.setTimeout(onComplete, 40);
      });

    completionTimeout = window.setTimeout(() => {
      revokeObjectURL(activeURLRef.current);
      activeURLRef.current = null;
      setGifSource(null);
      onComplete();
    }, dragonDurationMs);

    return () => {
      active = false;
      window.clearTimeout(completionTimeout);
      revokeObjectURL(activeURLRef.current);
      activeURLRef.current = null;
      setGifSource(null);
    };
  }, [event, onComplete]);

  if (event === null) {
    return null;
  }

  return (
    <div style={overlay}>
      <div aria-hidden style={dragonWrapper}>
        {gifSource !== null && (
          <img
            key={event.id}
            src={gifSource}
            alt=""
            style={dragonImage}
          />
        )}
      </div>
    </div>
  );
};

const loadDragonGifBlob = async (): Promise<Blob> => {
  if (dragonGifBlob !== null) {
    return dragonGifBlob;
  }

  if (dragonGifPromise !== null) {
    return dragonGifPromise;
  }

  dragonGifPromise = window.fetch(dragonGifSource, { cache: "force-cache" })
    .then(async response => {
      if (!response.ok) {
        throw new Error("failed to fetch dragon gif");
      }

      const blob = await response.blob();
      dragonGifBlob = blob;
      dragonGifPromise = null;
      return blob;
    })
    .catch(error => {
      dragonGifPromise = null;
      throw error;
    });

  return dragonGifPromise;
};

const revokeObjectURL = (objectURL: string | null) => {
  if (objectURL === null) {
    return;
  }

  window.URL.revokeObjectURL(objectURL);
};

const overlay: CSS.Properties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 20,
  overflow: "hidden",
};

const dragonWrapper: CSS.Properties = {
  position: "absolute",
  left: "calc(50% - 260px)",
  top: "47%",
  width: `${dragonWidth}px`,
  height: `${dragonHeight}px`,
  transform: "translateY(-50%)",
  filter: "drop-shadow(0 10px 16px rgba(0, 0, 0, 0.3))",
};

const dragonImage: CSS.Properties = {
  width: "100%",
  height: "100%",
  display: "block",
  objectFit: "contain",
};
